import React, { useState, useEffect } from 'react';
import { Paper, Box, TextField, Grid, Typography, Button, Card, CardContent, IconButton, Alert, InputAdornment, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  LocalCafe as CoffeeIcon,
  LunchDining as BeefIcon,
  LocalFireDepartment as FlameIcon,
  Spa as LeafIcon,
  Favorite as HeartIcon,
  Group as UsersIcon,
  Star as StarIcon,
  CardGiftcard as GiftIcon,
  AutoAwesome as SparklesIcon,
  Notifications as BellIcon,
  Mail as MailIcon,
  Label as TagIcon,
  Bolt as ZapIcon,
  Security as ShieldIcon,
  LocalShipping as TruckIcon,
  Inventory2 as PackageIcon,
  Place as MapPinIcon,
  Schedule as ClockIcon,
  Storefront as StoreIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';
import ImageSelector from '../components/ImageSelector';

const isImagePath = (value) => value && typeof value === 'string' && !value.startsWith('http');

const VALUE_ICON_OPTIONS = [
  { value: 'coffee', Icon: CoffeeIcon, labelKey: 'about_value_icon_coffee' },
  { value: 'beef', Icon: BeefIcon, labelKey: 'about_value_icon_beef' },
  { value: 'flame', Icon: FlameIcon, labelKey: 'about_value_icon_flame' },
  { value: 'leaf', Icon: LeafIcon, labelKey: 'about_value_icon_leaf' },
  { value: 'heart', Icon: HeartIcon, labelKey: 'about_value_icon_heart' },
  { value: 'users', Icon: UsersIcon, labelKey: 'about_value_icon_users' },
  { value: 'star', Icon: StarIcon, labelKey: 'newsletter_benefit_icon_star' },
  { value: 'gift', Icon: GiftIcon, labelKey: 'newsletter_benefit_icon_gift' },
  { value: 'sparkles', Icon: SparklesIcon, labelKey: 'newsletter_benefit_icon_sparkles' },
  { value: 'bell', Icon: BellIcon, labelKey: 'newsletter_benefit_icon_bell' },
  { value: 'mail', Icon: MailIcon, labelKey: 'newsletter_benefit_icon_mail' },
  { value: 'tag', Icon: TagIcon, labelKey: 'newsletter_benefit_icon_tag' },
  { value: 'zap', Icon: ZapIcon, labelKey: 'newsletter_benefit_icon_zap' },
  { value: 'shield', Icon: ShieldIcon, labelKey: 'newsletter_benefit_icon_shield' },
  { value: 'truck', Icon: TruckIcon, labelKey: 'about_value_icon_truck' },
  { value: 'package', Icon: PackageIcon, labelKey: 'about_value_icon_package' },
  { value: 'mapPin', Icon: MapPinIcon, labelKey: 'about_value_icon_map_pin' },
  { value: 'clock', Icon: ClockIcon, labelKey: 'about_value_icon_clock' },
  { value: 'store', Icon: StoreIcon, labelKey: 'about_value_icon_store' },
  { value: 'creditCard', Icon: CreditCardIcon, labelKey: 'about_value_icon_credit_card' },
  { value: 'checkCircle', Icon: CheckCircleIcon, labelKey: 'about_value_icon_check_circle' },
];

const AboutTab = ({ leadingContent }) => {
  const { t } = useTranslation();
  const { config, handleChange, handleAddArrayItem, handleArrayChange, handleRemoveArrayItem, isFieldConfigurable, isFieldFixed, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false);
  const [imageSelectorIndex, setImageSelectorIndex] = useState(null);
  const [previewUrls, setPreviewUrls] = useState({});

  const aboutPath = 'siteConfig.about';
  const aboutSectionsPath = 'siteConfig.about.sections';
  const aboutValuesPath = 'siteConfig.about.values';
  const isTitleConfigurable = isFieldConfigurable(`${aboutPath}.title`);
  const isSubtitleConfigurable = isFieldConfigurable(`${aboutPath}.subtitle`);
  const isDescriptionConfigurable = isFieldConfigurable(`${aboutPath}.description`);
  const isIconConfigurable = isFieldConfigurable(`${aboutPath}.icon`);
  const isSectionsConfigurable = isFieldConfigurable(aboutSectionsPath);
  const isValuesConfigurable = isFieldConfigurable(aboutValuesPath);
  const isValuesFixed = isFieldFixed(aboutValuesPath);

  const handleOpenImageSelector = (index) => {
    setImageSelectorIndex(index);
    setImageSelectorOpen(true);
  };

  const handleImageSelect = (imageUrl) => {
    if (imageSelectorIndex !== null) {
      const newSections = [...(config.siteConfig.about.sections || [])];
      if (!newSections[imageSelectorIndex]) newSections[imageSelectorIndex] = { image: '', text: '' };
      newSections[imageSelectorIndex] = { ...newSections[imageSelectorIndex], image: imageUrl };
      handleChange('siteConfig.about.sections', newSections);
    }
    setImageSelectorOpen(false);
    setImageSelectorIndex(null);
  };

  const sectionImages = config?.siteConfig?.about?.sections?.map((s) => s?.image).filter(Boolean) || [];
  const imagePaths = sectionImages.filter(isImagePath);
  const pathsKey = JSON.stringify(imagePaths);
  useEffect(() => {
    if (imagePaths.length === 0) {
      setPreviewUrls({});
      return;
    }
    let cancelled = false;
    const load = async () => {
      const next = {};
      for (const p of imagePaths) {
        if (cancelled) return;
        const url = await SitesService.getImagePreviewUrl(p);
        if (!cancelled && url) next[p] = url;
      }
      if (!cancelled) setPreviewUrls((prev) => ({ ...prev, ...next }));
    };
    load();
    return () => { cancelled = true; };
  }, [pathsKey]);

  const getPreviewSrc = (value) => {
    if (!value) return null;
    if (!isImagePath(value)) return value;
    return previewUrls[value] || null;
  };
  
  const hasEditableFields = isTitleConfigurable || isSubtitleConfigurable || isDescriptionConfigurable || isIconConfigurable || isSectionsConfigurable || (isValuesConfigurable && !isValuesFixed);
  
  if (!hasEditableFields) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const updates = {};
      
      // Campos simples
      if (isTitleConfigurable) {
        updates[`${aboutPath}.title`] = config.siteConfig.about.title;
      }
      if (isSubtitleConfigurable) {
        updates[`${aboutPath}.subtitle`] = config.siteConfig.about.subtitle;
      }
      if (isDescriptionConfigurable) {
        updates[`${aboutPath}.description`] = config.siteConfig.about.description;
      }
      if (isIconConfigurable) {
        updates[`${aboutPath}.icon`] = config.siteConfig.about.icon || 'heart';
      }
      if (isSectionsConfigurable) {
        updates[aboutSectionsPath] = config.siteConfig.about.sections ?? [];
      }
      
      // Array de values
      if (isValuesConfigurable && !isValuesFixed) {
        updates[aboutValuesPath] = config.siteConfig.about.values;
      }

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables en esta sección');
        return;
      }

      const preparedUpdates = prepareFieldUpdates(updates);
      await SitesService.updateFields(preparedUpdates);
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving about:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {leadingContent}

      {/* Card: Título, información principal y descripción */}
      {(isTitleConfigurable || isSubtitleConfigurable || isDescriptionConfigurable || isIconConfigurable) && (
        <Paper sx={{ padding: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('about_card_params') || 'Información principal'}
          </Typography>
          <Grid container spacing={2}>
            {isIconConfigurable && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('about_header_icon') || 'Icono superior'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {VALUE_ICON_OPTIONS.map((opt) => {
                    const selected = (config.siteConfig.about.icon || 'heart') === opt.value;
                    const IconComponent = opt.Icon;
                    return (
                      <Tooltip key={opt.value} title={t(opt.labelKey) || opt.value}>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => handleChange('siteConfig.about.icon', opt.value)}
                          sx={{
                            width: 40,
                            height: 40,
                            p: 0,
                            minWidth: 0,
                            border: '1px solid',
                            borderColor: selected ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            bgcolor: selected ? 'primary.main' : 'transparent',
                            color: selected ? 'primary.contrastText' : 'text.secondary',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              bgcolor: selected ? 'primary.dark' : 'action.hover',
                              color: selected ? 'primary.contrastText' : 'text.primary',
                            },
                          }}
                        >
                          <IconComponent sx={{ fontSize: 22 }} />
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Grid>
            )}
            {isTitleConfigurable && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('about_title') || 'Título'}
                  value={config.siteConfig.about.title || ''}
                  onChange={(e) => handleChange('siteConfig.about.title', e.target.value)}
                />
              </Grid>
            )}
            {isSubtitleConfigurable && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('about_info') || 'Info'}
                  value={config.siteConfig.about.subtitle || ''}
                  onChange={(e) => handleChange('siteConfig.about.subtitle', e.target.value)}
                />
              </Grid>
            )}
            {isDescriptionConfigurable && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={4}
                  label={t('about_description') || 'Descripción'}
                  value={config.siteConfig.about.description || ''}
                  onChange={(e) => handleChange('siteConfig.about.description', e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Card 3: Categorías (valores) */}
      {isValuesConfigurable && !isValuesFixed && (
        <Paper sx={{ padding: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('about_card_categories') || 'Categorías'}
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => handleAddArrayItem('siteConfig.about.values', { title: '', description: '', icon: 'coffee' })}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            {t('add_value') || 'Agregar Valor'}
          </Button>
          {config.siteConfig.about.values?.map((value, index) => {
            const currentIcon = value?.icon || 'coffee';
            const isValidIcon = VALUE_ICON_OPTIONS.some((opt) => opt.value === currentIcon);
            const displayIcon = isValidIcon ? currentIcon : 'coffee';
            return (
              <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle1">{t('value') || 'Valor'} {index + 1}</Typography>
                    <IconButton
                      onClick={() => handleRemoveArrayItem('siteConfig.about.values', index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box sx={{ flex: '33 1 0%', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {t('value_icon') || 'Icono'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {VALUE_ICON_OPTIONS.map((opt) => {
                          const selected = displayIcon === opt.value;
                          const IconComponent = opt.Icon;
                          return (
                            <Tooltip key={opt.value} title={t(opt.labelKey) || opt.value}>
                              <Box
                                component="button"
                                type="button"
                                onClick={() => handleArrayChange('siteConfig.about.values', index, 'icon', opt.value)}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  p: 0,
                                  minWidth: 0,
                                  border: '1px solid',
                                  borderColor: selected ? 'primary.main' : 'divider',
                                  borderRadius: 1,
                                  bgcolor: selected ? 'primary.main' : 'transparent',
                                  color: selected ? 'primary.contrastText' : 'text.secondary',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  '&:hover': {
                                    bgcolor: selected ? 'primary.dark' : 'action.hover',
                                    color: selected ? 'primary.contrastText' : 'text.primary',
                                  },
                                }}
                              >
                                <IconComponent sx={{ fontSize: 22 }} />
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </Box>
                    <Box sx={{ flex: '33 1 0%', minWidth: 120 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('value_title') || 'Título'}
                        value={value.title || ''}
                        onChange={(e) => handleArrayChange('siteConfig.about.values', index, 'title', e.target.value)}
                      />
                    </Box>
                    <Box sx={{ flex: '44 1 0%', minWidth: 120 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('value_description') || 'Descripción'}
                        value={value.description || ''}
                        onChange={(e) => handleArrayChange('siteConfig.about.values', index, 'description', e.target.value)}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Paper>
      )}

      {/* Card 4: Secciones imagen + texto */}
      {isSectionsConfigurable && (
        <Paper sx={{ padding: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('about_card_sections') || 'Secciones imagen + texto'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('about_sections_alternate') || 'Se alternan imagen izq/der en la página.'}
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => handleAddArrayItem(aboutSectionsPath, { image: '', text: '' })}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            {t('add_section') || 'Agregar sección'}
          </Button>
          {(config.siteConfig.about.sections || []).map((section, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="subtitle1">{t('section') || 'Sección'} {index + 1}</Typography>
                  <IconButton
                    onClick={() => handleRemoveArrayItem(aboutSectionsPath, index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  {(section.image && (getPreviewSrc(section.image) || !isImagePath(section.image))) && (
                    <Box sx={{ flexShrink: 0 }}>
                      <img
                        src={getPreviewSrc(section.image) || section.image}
                        alt={`Preview ${index + 1}`}
                        style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </Box>
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    sx={{ flex: 1, minWidth: 200 }}
                    label={t('image_url') || 'URL de imagen'}
                    value={section.image || ''}
                    onChange={(e) => handleArrayChange(aboutSectionsPath, index, 'image', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleOpenImageSelector(index)} edge="end" color="primary">
                            <ImageIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={4}
                  label={t('about_section_text') || 'Texto'}
                  value={section.text || ''}
                  onChange={(e) => handleArrayChange(aboutSectionsPath, index, 'text', e.target.value)}
                />
              </CardContent>
            </Card>
          ))}
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
          {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
        </Button>
      </Box>
      {isSectionsConfigurable && (
        <ImageSelector
          open={imageSelectorOpen}
          onClose={() => { setImageSelectorOpen(false); setImageSelectorIndex(null); }}
          onSelect={handleImageSelect}
          currentValue={imageSelectorIndex !== null ? (config.siteConfig.about.sections?.[imageSelectorIndex]?.image || '') : ''}
          label={t('select_about_section_image') || 'Seleccionar imagen de sección'}
        />
      )}
    </Box>
  );
};

export default AboutTab;
